import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import StarIcon from '@mui/icons-material/Star';
import WarningIcon from '@mui/icons-material/Warning';
import { Slide, alpha, createTheme, darken, decomposeColor, lighten } from '@mui/material';
import type { ThemeOptions } from '@mui/material';
import defaultsDeep from 'lodash/defaultsDeep';
import { Fragment } from 'react';
import { contentMaxWidth, responiveFont } from './base-theme';
import { baseColorScheme } from './color-scheme';
import { getButtonRadiusValue, getContainerRadiusValue, getFormItemRadiusValue, getModalAndLayerRadiusValue } from './generate-theme-radius-helper';
import { splitSchemePalette } from './scheme-palette';
import type { AppTheme } from './types';

// Medium (default) form fields: 12px of padding on every side around a 24px
// line height, which lands them at the same 48px height as medium buttons.
const FIELD_PADDING = '12px';
const FIELD_LINE_HEIGHT = '24px';
// Room for the select's dropdown indicator: the 24px icon sits 8px from the
// edge, so the text has to stop before it.
const SELECT_INDICATOR_SPACE = '32px';
export function generateTheme(appConfig: any, colorScheme: 'light' | 'dark'): ThemeOptions {
  const isDark = colorScheme === 'dark';
  // `theme` describes the scheme its `colorScheme` names, light when unset.
  // Rendering that scheme takes it as is. Rendering dark over a light `theme`
  // merges `darkTheme` on top and leaves out the base theme's text, surface
  // and border colours, which only describe the light scheme. Merge into a
  // fresh object so the app config is never mutated.
  const isBaseScheme = colorScheme === baseColorScheme(appConfig);
  const theme: AppTheme = defaultsDeep({}, isDark && !isBaseScheme ? appConfig.darkTheme : {}, isBaseScheme ? appConfig.theme : splitSchemePalette(appConfig.theme ?? {}).shared);
  const {
    fontFamily,
    primaryColor,
    secondaryColor,
    borderRadius,
    modalAndLayerRadius,
    containerRadius,
    formItemRadius,
    buttonRadius,
    customStyles,
    iosSwitches,
    animations,
    toastAnimation,
    toastStyle,
    modalAnimation,
    shadows: shadowStrength,
    actionBar,
    formFieldStyle,
    palette: configPalette
  } = theme;
  const dark = createTheme({
    palette: {
      mode: 'dark'
    }
  }).palette;
  const {
    breakpoints,
    palette: {
      augmentColor
    }
  } = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536
      }
    }
  });

  // Figma's `*/Contrast` tokens alias Common/White in light mode and
  // Common/Black in dark mode, and that pair mirrors around the mode — so the
  // label on a filled surface is white in both schemes. This is the default
  // for the semantic fills below, whose shades the design system fixes; an
  // app config that supplies its own color supplies its own contrast text
  // with it, or lets MUI derive one.
  const filledContrastText = '#fff';

  // Fallbacks for every palette entry the app config leaves out. Anything
  // `appConfig.theme.palette` (or `appConfig.darkTheme.palette`) declares wins
  // over the matching value here, down to the individual color.
  const defaultPalette: ThemeOptions['palette'] = {
    primary: {
      main: primaryColor ?? '#1976d2'
    },
    secondary: {
      // No contrast text here, as for `primary`: both are brand colors an app
      // config picks, so MUI derives the label color from the shade it is
      // actually given. A config that wants a specific one declares it.
      main: secondaryColor ?? '#dc004e'
    },
    error: {
      main: '#FA4517',
      // Sunrise 500
      light: isDark ? '#C83712' : '#FC8F74',
      // Sunrise 300
      dark: isDark ? '#FC8F74' : '#C83712',
      // Sunrise 700
      contrastText: filledContrastText,
      alertContent: isDark ? '#FC8F74' : '#C83712' // Sunrise 700
    },
    info: {
      main: isDark ? '#2F729C' : '#0D5B8C',
      // Navy 600 / Navy 500
      light: isDark ? '#094164' : '#5289AC',
      // Navy 300
      dark: isDark ? '#5289AC' : '#094164',
      // Navy 700
      contrastText: filledContrastText,
      alertContent: isDark ? '#74A1BD' : '#094164' // Navy 800 / Navy 700
    },
    divider: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    action: {
      active: isDark ? 'rgba(255, 255, 255, 0.44)' : 'rgba(0, 0, 0, 0.44)',
      hover: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
      selected: isDark ? dark.action.selected : alpha('#000', 0.08),
      disabled: isDark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.14)',
      disabledBackground: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'
    },
    text: isDark ? {
      primary: 'rgba(255, 255, 255, 0.87)',
      secondary: 'rgba(255, 255, 255, 0.6)',
      disabled: 'rgba(255, 255, 255, 0.4)'
    } : {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0,0,0,0.6)',
      disabled: 'rgba(0,0,0,0.4)'
    },
    warning: {
      main: '#FE9418',
      // Orange 500
      light: isDark ? '#98580E' : '#FEBE74',
      // Orange 300
      dark: isDark ? '#FED4A2' : '#98580E',
      // Orange 700
      contrastText: filledContrastText,
      alertContent: isDark ? '#FED4A2' : '#98580E' // Orange 700
    },
    background: {
      default: isDark ? '#121212' : '#fff',
      paper: isDark ? '#202020' : '#fff',
      elevated: isDark ? '#303030' : '#fff'
    },
    success: {
      main: '#10AF6A',
      // Green 500
      light: isDark ? '#008E53' : '#6BC796',
      // Green 300
      dark: isDark ? '#6BC796' : '#008E53',
      // Green 700
      contrastText: filledContrastText,
      alertContent: isDark ? '#6BC796' : '#008E53' // Green 700
    },
    monochrome: {
      light: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      main: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
      dark: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)',
      contrastText: isDark ? 'rgba(255,255,255,0.87)' : 'rgba(0, 0, 0, 0.87)',
      // A tint rather than a solid grey, so the alert shades whatever surface
      // it sits on. `alertContent` defaults to `text.primary` after the merge.
      alertBackground: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
      outlineRestingBorder: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' // Monochrome/States/Outline Resting Border
    },
    white: {
      main: isDark ? '#303030' : '#fff',
      contrastText: isDark ? 'rgba(255,255,255,0.87)' : 'rgba(0, 0, 0, 0.87)'
    },
    black: {
      main: '#000',
      dark: isDark ? '#242424' : '#313131',
      contrastText: '#fff'
    },
    outlinedBorder: {
      main: isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.16)'
    },
    backdropOverlay: {
      main: 'rgba(0, 0, 0, 0.5)'
    },
    filledInputBackground: {
      main: isDark ? 'rgba(255, 255, 255, 0.09)' : 'rgba(0, 0, 0, 0.09)'
    },
    standardInputLine: {
      main: isDark ? 'rgba(255, 255, 255, 0.42)' : 'rgba(0, 0, 0, 0.42)'
    },
    snackbarBackground: {
      main: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)'
    },
    ratingActive: {
      main: '#FFBB00' // Other/Rating Active
    },
    containerBackground: {
      main: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'
    }
  };

  // A status colour whose main the app config sets drops the default ramp,
  // which belongs to a different colour, so `createTheme` derives its light,
  // dark and contrast shades from that main the way it does for primary and
  // secondary. Alert text follows the shade that reads on the scheme's tinted
  // alert background, as the default ramp's does.
  const tonalOffset = configPalette?.tonalOffset ?? 0.2;
  const lightOffset = typeof tonalOffset === 'number' ? tonalOffset : tonalOffset.light;
  const darkOffset = typeof tonalOffset === 'number' ? tonalOffset * 1.5 : tonalOffset.dark;
  for (const channel of ['error', 'info', 'warning', 'success'] as const) {
    const configColor = configPalette?.[channel] as {
      main?: string;
      light?: string;
      dark?: string;
    } | undefined;
    if (configColor?.main) {
      defaultPalette[channel] = {
        main: configColor.main,
        alertContent: isDark ? configColor.light ?? lighten(configColor.main, lightOffset) : configColor.dark ?? darken(configColor.main, darkOffset)
      };
    }
  }
  // Monochrome likewise, keeping its alert background and resting border.
  // Its shades come from `augmentColor`, which only knows MUI's default
  // offset, so they are worked out here with the theme's own.
  const configMonochrome = configPalette?.monochrome as {
    main?: string;
  } | undefined;
  if (configMonochrome?.main) {
    const {
      alertBackground,
      outlineRestingBorder,
      contrastText
    } = defaultPalette.monochrome as {
      alertBackground?: string;
      outlineRestingBorder?: string;
      contrastText?: string;
    };
    // `augmentColor` picks contrast text from the colour's RGB alone, so a
    // translucent main (a tint over the surface) keeps the scheme's own.
    const {
      values
    } = decomposeColor(configMonochrome.main);
    const translucent = values.length > 3 && Number(values[3]) < 1;
    defaultPalette.monochrome = {
      main: configMonochrome.main,
      light: lighten(configMonochrome.main, lightOffset),
      dark: darken(configMonochrome.main, darkOffset),
      ...(translucent && {
        contrastText
      }),
      alertBackground,
      outlineRestingBorder
    };
  }
  const mergedPalette = defaultsDeep({}, configPalette, defaultPalette);
  const wantedPalette: ThemeOptions['palette'] = {
    ...mergedPalette,
    // The scheme drives the mode, so a palette can't opt out of it.
    mode: colorScheme,
    // `createTheme` only derives light/dark/contrastText for the standard
    // channels, so the custom ones are augmented here — after the merge, so the
    // derived shades follow whatever the app config supplied.
    monochrome: augmentColor({
      color: {
        // Monochrome alert text follows the theme's text unless set.
        alertContent: mergedPalette.text?.primary,
        ...mergedPalette.monochrome
      }
    }),
    white: augmentColor({
      color: mergedPalette.white
    }),
    black: augmentColor({
      color: mergedPalette.black
    })
  };
  const {
    palette
  } = createTheme({
    palette: wantedPalette
  });

  // The design's shadow opacities, scaled by the theme's shadow strength.
  // Every shadow the theme draws is built from these three, so `off` takes
  // them all away.
  const shadowScale = {
    off: 0,
    low: 0.5,
    medium: 1,
    high: 2
  }[shadowStrength ?? 'medium'];
  const [shadowA, shadowB, shadowC] = (isDark ? [0.2, 0.14, 0.12] : [0.05, 0.04, 0.04]).map(opacity => `rgba(0, 0, 0, ${Math.round(Math.min(opacity * shadowScale, 1) * 1000) / 1000})`);

  // The MUI variant every text field and select takes unless it names its
  // own. `stroked` is MUI's standard field, an underline only.
  const fieldVariant = ({
    outlined: 'outlined',
    filled: 'filled',
    stroked: 'standard'
  } as const)[formFieldStyle ?? 'outlined'];
  const shape = {
    borderRadius: (() => {
      switch (borderRadius) {
        case 'none':
          return 0;
        case 'small':
          return 6;
        case 'large':
          return 12;
        default:
          return 6;
      }
    })()
  };
  const modalAndLayerRadiusValue = getModalAndLayerRadiusValue(modalAndLayerRadius);
  const containerRadiusValue = getContainerRadiusValue(containerRadius);
  const formItemRadiusValue = getFormItemRadiusValue(formItemRadius);
  // A filled field only rounds its top corners, above the underline, so a
  // pill radius is capped rather than bulging the top into a half circle.
  const filledFieldRadius = Math.min(Number(formItemRadiusValue.main), 12);
  const buttonRadiusValue = getButtonRadiusValue(buttonRadius);
  // A text skeleton is drawn at `scale(1, 0.6)`, so its vertical radius is
  // stretched to compensate, as MUI does for its own default.
  const scaledTextRadius = (radius: number | string) => {
    const value = Number.parseFloat(String(radius));
    return `${value}px / ${Math.round(value / 0.6 * 10) / 10}px`;
  };
  const result: ThemeOptions = {
    modalAndLayerRadius: modalAndLayerRadiusValue,
    containerRadius: containerRadiusValue,
    formItemRadius: formItemRadiusValue,
    buttonRadius: buttonRadiusValue,
    toastAnimation: toastAnimation ?? 'slideSide',
    toastStyle: toastStyle ?? 'soft',
    actionBar: actionBar ?? 'floating',
    palette: wantedPalette,
    breakpoints: {
      values: {
        ...breakpoints.values,
        lg: 1312
      }
    },
    // Every MUI transition is built through `transitions.create`, so with
    // animations off they all resolve to none and changes land instantly. The
    // durations go to zero as well, so modals, drawers and menus also mount
    // and unmount without waiting out a transition, and toasts can read them.
    ...(animations === false && {
      transitions: {
        create: () => 'none',
        duration: {
          shortest: 0,
          shorter: 0,
          short: 0,
          standard: 0,
          complex: 0,
          enteringScreen: 0,
          leavingScreen: 0
        }
      }
    }),
    typography: {
      fontFamily: `${fontFamily},"Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif`,
      h1: {
        fontWeight: 700,
        lineHeight: 1.05,
        letterSpacing: '-1.5px',
        ...responiveFont(42, 96)
      },
      h2: {
        fontWeight: 700,
        lineHeight: 1.05,
        letterSpacing: '-1px',
        ...responiveFont(40, 60)
      },
      h3: {
        fontWeight: 700,
        lineHeight: 1.05,
        letterSpacing: '-0.75px',
        ...responiveFont(32, 48)
      },
      h4: {
        fontWeight: 700,
        lineHeight: 1.05,
        letterSpacing: '-0.5px',
        ...responiveFont(26, 32)
      },
      h5: {
        fontWeight: 700,
        lineHeight: 1.1,
        ...responiveFont(18, 24)
      },
      h6: {
        fontWeight: 700,
        lineHeight: 1.1,
        ...responiveFont(16, 18)
      },
      subtitle1: {
        fontWeight: 400,
        lineHeight: 1.4,
        letterSpacing: '0.15px',
        ...responiveFont(15, 16)
      },
      button: {
        textTransform: (theme.buttonTextTransform ?? 'none') as 'none',
        fontWeight: 500
      },
      body1Medium: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.5,
        letterSpacing: '0.15px',
        fontFamily: `${fontFamily},"Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif`
      },
      body1Bold: {
        fontSize: '1rem',
        fontWeight: 700,
        lineHeight: 1.5,
        letterSpacing: '0.15px',
        fontFamily: `${fontFamily},"Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif`
      },
      body2Medium: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.43,
        letterSpacing: '0.15px',
        fontFamily: `${fontFamily},"Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif`
      },
      body2Bold: {
        fontSize: '0.875rem',
        fontWeight: 700,
        lineHeight: 1.43,
        letterSpacing: '0.15px',
        fontFamily: `${fontFamily},"Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif`
      },
      body3: {
        fontSize: '0.813rem',
        fontWeight: 400,
        lineHeight: 1.4,
        letterSpacing: '0.15px',
        fontFamily: `${fontFamily},"Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif`
      },
      body3Medium: {
        fontSize: '0.813rem',
        fontWeight: 500,
        lineHeight: 1.4,
        letterSpacing: '0.15px',
        fontFamily: `${fontFamily},"Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif`
      },
      body3Bold: {
        fontSize: '0.813rem',
        fontWeight: 700,
        lineHeight: 1.4,
        letterSpacing: '0.15px',
        fontFamily: `${fontFamily},"Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif`
      }
    },
    shape,
    shadows: ['none', `0px 2px 1px -1px ${shadowA}, 0px 1px 1px ${shadowB}, 0px 1px 3px ${shadowC}`, `0px 3px 1px -2px ${shadowA}, 0px 2px 4px ${shadowB}, 0px 1px 6px ${shadowC}`, `0px 3px 3px -2px ${shadowA}, 0px 3px 6px ${shadowB}, 0px 1px 10px ${shadowC}`, `0px 2px 4px -1px ${shadowA}, 0px 4px 6px ${shadowB}, 0px 1px 12px ${shadowC}`, `0px 3px 5px -1px ${shadowA}, 0px 5px 8px ${shadowB}, 0px 1px 14px ${shadowC}`, `0px 3px 5px -1px ${shadowA}, 0px 6px 10px ${shadowB}, 0px 1px 18px ${shadowC}`, `0px 4px 5px -2px ${shadowA}, 0px 7px 10px 1px ${shadowB}, 0px 2px 16px 1px ${shadowC}`, `0px 5px 5px -3px ${shadowA}, 0px 8px 10px 1px ${shadowB}, 0px 3px 14px 2px ${shadowC}`, `0px 5px 6px -3px ${shadowA}, 0px 9px 12px 1px ${shadowB}, 0px 3px 16px 2px ${shadowC}`, `0px 6px 6px -3px ${shadowA}, 0px 10px 14px 1px ${shadowB}, 0px 4px 18px 3px ${shadowC}`, `0px 6px 7px -4px ${shadowA}, 0px 11px 15px 1px ${shadowB}, 0px 4px 20px 3px ${shadowC}`, `0px 7px 8px -4px ${shadowA}, 0px 12px 17px 2px ${shadowB}, 0px 5px 22px 4px ${shadowC}`, `0px 7px 8px -4px ${shadowA}, 0px 13px 19px 2px ${shadowB}, 0px 5px 24px 4px ${shadowC}`, `0px 7px 9px -4px ${shadowA}, 0px 14px 21px 2px ${shadowB}, 0px 5px 26px 4px ${shadowC}`, `0px 8px 9px -5px ${shadowA}, 0px 15px 22px 2px ${shadowB}, 0px 6px 28px 5px ${shadowC}`, `0px 8px 10px -5px ${shadowA}, 0px 16px 24px 2px ${shadowB}, 0px 6px 30px 5px ${shadowC}`, `0px 8px 11px -5px ${shadowA}, 0px 17px 26px 2px ${shadowB}, 0px 6px 32px 5px ${shadowC}`, `0px 9px 11px -5px ${shadowA}, 0px 18px 28px 2px ${shadowB}, 0px 7px 34px 6px ${shadowC}`, `0px 9px 12px -6px ${shadowA}, 0px 19px 29px 2px ${shadowB}, 0px 7px 36px 6px ${shadowC}`, `0px 10px 13px -6px ${shadowA}, 0px 20px 31px 3px ${shadowB}, 0px 8px 38px 7px ${shadowC}`, `0px 10px 13px -6px ${shadowA}, 0px 21px 33px 3px ${shadowB}, 0px 8px 40px 7px ${shadowC}`, `0px 10px 14px -6px ${shadowA}, 0px 22px 35px 3px ${shadowB}, 0px 8px 42px 7px ${shadowC}`, `0px 11px 14px -7px ${shadowA}, 0px 23px 36px 3px ${shadowB}, 0px 9px 44px 8px ${shadowC}`, `0px 11px 15px -7px ${shadowA}, 0px 24px 38px 3px ${shadowB}, 0px 9px 46px 8px ${shadowC}`],
    components: {
      MuiContainer: {
        styleOverrides: {
          // The content column is 1312px wide; the container adds its
          // gutters on top of that.
          maxWidthLg: ({
            theme
          }) => ({
            [theme.breakpoints.up('lg')]: {
              maxWidth: contentMaxWidth
            }
          }),
          root: ({
            theme,
            ownerState
          }) => ownerState.disableGutters ? {} : {
            paddingLeft: theme.spacing(2.5),
            paddingRight: theme.spacing(2.5),
            [theme.breakpoints.up('sm')]: {
              paddingLeft: theme.spacing(3),
              paddingRight: theme.spacing(3)
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: ({
            theme
          }) => ({
            borderRadius: buttonRadiusValue.main,
            [theme.breakpoints.down('sm')]: {
              borderRadius: buttonRadiusValue.mobile
            },
            height: '32px',
            minWidth: '32px'
          }),
          label: {
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '18px',
            letterSpacing: '0.16px'
          }
        },
        variants: [{
          props: {
            size: 'small'
          },
          style: {
            height: '24px',
            minWidth: '24px'
          }
        }, {
          props: {
            color: 'warning'
          },
          style: {
            color: palette.warning.contrastText
          }
        }, {
          props: {
            color: 'secondary'
          },
          style: {
            color: palette.secondary.contrastText
          }
        }]
      },
      MuiCard: {
        styleOverrides: {
          root: ({
            theme
          }) => ({
            borderRadius: containerRadiusValue.main,
            [theme.breakpoints.down('sm')]: {
              borderRadius: containerRadiusValue.mobile
            }
          })
        }
      },
      MuiSkeleton: {
        styleOverrides: {
          root: ({
            theme
          }) => ({
            backgroundColor: theme.palette.action.disabledBackground,
            variants: [
            // MUI rounds `text` by `shape`. A line of text takes the form
            // item radius, and keeps MUI's elliptical corner so the
            // `scale(1, 0.6)` it is drawn at doesn't squash it.
            {
              props: {
                variant: 'text'
              },
              style: {
                borderRadius: scaledTextRadius(formItemRadiusValue.main),
                [theme.breakpoints.down('sm')]: {
                  borderRadius: scaledTextRadius(formItemRadiusValue.mobile)
                }
              }
            },
            // MUI rounds `rounded` by `shape`. Most stand in for a card, so
            // they take the container radius instead.
            {
              props: {
                variant: 'rounded'
              },
              style: {
                borderRadius: containerRadiusValue.main,
                [theme.breakpoints.down('sm')]: {
                  borderRadius: containerRadiusValue.mobile
                }
              }
            }, {
              props: {
                variant: 'modalAndLayer'
              },
              style: {
                borderRadius: modalAndLayerRadiusValue.main,
                [theme.breakpoints.down('sm')]: {
                  borderRadius: modalAndLayerRadiusValue.mobile
                }
              }
            }, {
              props: {
                variant: 'container'
              },
              style: {
                borderRadius: containerRadiusValue.main,
                [theme.breakpoints.down('sm')]: {
                  borderRadius: containerRadiusValue.mobile
                }
              }
            }, {
              props: {
                variant: 'formItem'
              },
              style: {
                borderRadius: formItemRadiusValue.main,
                [theme.breakpoints.down('sm')]: {
                  borderRadius: formItemRadiusValue.mobile
                }
              }
            }, {
              props: {
                variant: 'button'
              },
              style: {
                borderRadius: buttonRadiusValue.main,
                [theme.breakpoints.down('sm')]: {
                  borderRadius: buttonRadiusValue.mobile
                }
              }
            }]
          })
        }
      },
      MuiButton: {
        // Buttons are flat by design; MUI's default elevation on `contained`
        // put a shadow under every primary action.
        defaultProps: {
          disableElevation: true
        },
        styleOverrides: {
          root: ({
            theme
          }) => ({
            borderRadius: buttonRadiusValue.main,
            [theme.breakpoints.down('sm')]: {
              borderRadius: buttonRadiusValue.mobile
            },
            '&.Mui-focusVisible': {
              outline: `1px solid ${palette.primary.main}`
            },
            // MUI keeps the `Mui-focusVisible` class until blur, so the ring can
            // linger after a click. Narrowing it to the live `:focus-visible`
            // state keeps the ring for keyboard focus and drops it for pointer
            // interaction.
            '&.Mui-focusVisible:not(:focus-visible)': {
              outline: 'none'
            },
            ':disabled': {
              backgroundColor: palette.action.disabledBackground,
              '& .MuiSvgIcon-root': {
                color: palette.action.disabled
              }
            },
            variants: [{
              props: {
                color: 'monochrome'
              },
              style: {
                // Touch devices hold `:hover` after a tap until the next tap
                // elsewhere, which would leave the fill stuck on the button.
                // Gating it on a hovering pointer keeps the effect for mouse
                // users and drops it for touch.
                '@media (hover: hover) and (pointer: fine)': {
                  ':hover': {
                    backgroundColor: palette.action.hover
                  }
                },
                color: palette.text.primary
              }
            }, {
              props: {
                color: 'monochrome',
                variant: 'contained'
              },
              style: {
                ':hover': {
                  boxShadow: 'none',
                  backgroundColor: palette.monochrome.dark,
                  color: palette.monochrome.contrastText
                },
                backgroundColor: palette.monochrome.main,
                boxShadow: 'none'
              }
            }, {
              props: {
                color: 'monochrome',
                variant: 'outlined'
              },
              style: {
                ':hover': {
                  borderColor: palette.action.selected
                },
                // A trigger whose menu or panel is open reads as active, with
                // the same fill hovering gives it.
                '&[aria-expanded="true"]': {
                  backgroundColor: palette.action.hover
                },
                borderColor: palette.action.selected,
                color: palette.text.primary
              }
            }, {
              props: {
                color: 'white',
                variant: 'contained'
              },
              style: {
                boxShadow: `0 0 4px -1px ${shadowA}, 0 4px 6px 0 ${shadowB}, 0 1px 12px 0 ${shadowC}`,
                color: palette.monochrome.contrastText,
                backgroundColor: palette.background.paper,
                '&:hover': {
                  boxShadow: `0 0 4px -1px ${shadowA}, 0 4px 6px 0 ${shadowB}, 0 1px 12px 0 ${shadowC}`,
                  // The hover tint is layered over the resting surface, so
                  // the button stays opaque and follows the theme's colours.
                  backgroundColor: palette.background.paper,
                  backgroundImage: `linear-gradient(${palette.action.hover}, ${palette.action.hover})`,
                  '.MuiSvgIcon-root': {
                    color: palette.monochrome.contrastText
                  }
                },
                // Stays opaque over the seat map: the disabled tint is laid
                // over the resting surface rather than replacing it.
                '&:disabled': {
                  backgroundColor: palette.background.paper,
                  backgroundImage: `linear-gradient(${palette.action.disabledBackground}, ${palette.action.disabledBackground})`,
                  color: palette.action.disabled,
                  boxShadow: `0 0 4px -1px ${shadowA}, 0 4px 6px 0 ${shadowB}, 0 1px 12px 0 ${shadowC}`
                },
                // This class is toggled manually (not via real keyboard
                // focus) to reflect the button's popup being open, so the
                // root style's `&.Mui-focusVisible:not(:focus-visible)`
                // reset always wins over an `outline` here. Render the
                // border as an inset box-shadow instead, which that reset
                // doesn't touch.
                '&.Mui-focusVisible': {
                  boxShadow: `0 0 4px -1px ${shadowA}, 0 4px 6px 0 ${shadowB}, 0 1px 12px 0 ${shadowC}, inset 0 0 0 1px ${palette.monochrome.outlineRestingBorder}`,
                  backgroundColor: palette.background.paper,
                  '.MuiSvgIcon-root': {
                    color: palette.monochrome.contrastText
                  }
                }
              }
            }, {
              props: {
                size: 'large'
              },
              style: {
                padding: '11px 22px',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '26px',
                letterSpacing: '0.2px'
              }
            }, {
              props: {
                size: 'medium'
              },
              style: {
                padding: '8px 16px',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '24px',
                letterSpacing: '0.1px'
              }
            }, {
              props: {
                variant: 'outlined'
              },
              style: {
                '&.Mui-disabled': {
                  borderColor: 'transparent'
                }
              }
            }, {
              props: {
                variant: 'outlined',
                size: 'large'
              },
              style: {
                padding: '10px 22px'
              }
            }, {
              props: {
                variant: 'outlined',
                size: 'medium'
              },
              style: {
                padding: '7px 16px'
              }
            }, {
              props: {
                variant: 'outlined',
                size: 'small'
              },
              style: {
                padding: '4px 12px'
              }
            }, {
              props: {
                size: 'small'
              },
              style: {
                padding: '5px 12px',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '22px'
              }
            },
            // Bold label is reserved for the primary call to action: a large
            // contained button in any color other than monochrome.
            {
              props: ({
                ownerState
              }) => ownerState.variant === 'contained' && ownerState.size === 'large' && ownerState.color !== 'monochrome' && ownerState.color !== 'white',
              style: {
                fontWeight: 700
              }
            }]
          })
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: !theme.buttonRipple
        }
      },
      MuiCardActionArea: {
        defaultProps: {
          disableRipple: !theme.buttonRipple
        }
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: ({
            theme
          }) => ({
            borderRadius: buttonRadiusValue.main,
            [theme.breakpoints.down('sm')]: {
              borderRadius: buttonRadiusValue.mobile
            }
          })
        },
        defaultProps: {
          disableRipple: !theme.buttonRipple
        },
        variants: [
        // MUI derives the outlined borders from the color's `main`, which
        // for monochrome is a near-transparent fill.
        {
          props: {
            color: 'monochrome',
            variant: 'outlined'
          },
          style: {
            '& .MuiButtonGroup-grouped': {
              borderColor: palette.monochrome.outlineRestingBorder
            }
          }
        }]
      },
      MuiList: {
        styleOverrides: {
          root: {
            paddingLeft: 8,
            paddingRight: 8
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            paddingTop: '8px',
            paddingBottom: '8px',
            fontWeight: 500,
            borderRadius: formItemRadiusValue.main,
            fontSize: '14px',
            lineHeight: '24px',
            gap: '8px'
          }
        }
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            paddingTop: '24px',
            paddingBottom: '24px',
            [breakpoints.down('sm')]: {
              paddingTop: '10px',
              paddingBottom: '10px'
            }
          }
        }
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            fontWeight: 700
          }
        }
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 500
          }
        }
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            fontSize: '15px',
            fontWeight: 700,
            lineHeight: 1.5,
            letterSpacing: '0.15px',
            marginBottom: '4px'
          }
        }
      },
      MuiAlert: {
        defaultProps: {
          iconMapping: {
            error: <ErrorIcon fontSize="inherit" />,
            warning: <WarningIcon fontSize="inherit" />,
            info: <InfoIcon fontSize="inherit" />,
            success: <CheckCircleIcon fontSize="inherit" />,
            monochrome: <InfoIcon fontSize="inherit" />
          }
        },
        styleOverrides: {
          action: {
            padding: '3px 0 3px 16px',
            marginRight: 0
          },
          root: ({
            theme
          }) => ({
            borderRadius: containerRadiusValue.main,
            [theme.breakpoints.down('sm')]: {
              borderRadius: containerRadiusValue.mobile
            },
            // Alert content colors per severity for outlined & standard variants
            '&.MuiAlert-outlinedError, &.MuiAlert-standardError': {
              color: palette.error.alertContent
            },
            '&.MuiAlert-outlinedWarning, &.MuiAlert-standardWarning': {
              color: palette.warning.alertContent
            },
            '&.MuiAlert-outlinedInfo, &.MuiAlert-standardInfo': {
              color: palette.info.alertContent
            },
            '&.MuiAlert-outlinedSuccess, &.MuiAlert-standardSuccess': {
              color: palette.success.alertContent
            },
            // Monochrome: content color for all variants
            '&.MuiAlert-outlinedMonochrome, &.MuiAlert-standardMonochrome': {
              color: palette.monochrome.alertContent
            },
            // Monochrome icon: use alertContent instead of monochrome.main
            '&.MuiAlert-colorMonochrome .MuiAlert-icon': {
              color: palette.monochrome.alertContent
            },
            // A filled alert takes the severity's `main` shade. MUI reaches for
            // `dark` in dark mode, which the mirrored ramp makes the palest
            // step — far too pale to carry the white content color. Pinning
            // `main` keeps a filled alert identical in both schemes.
            '&.MuiAlert-filledError': {
              backgroundColor: palette.error.main
            },
            '&.MuiAlert-filledWarning': {
              backgroundColor: palette.warning.main
            },
            '&.MuiAlert-filledInfo': {
              backgroundColor: palette.info.main
            },
            '&.MuiAlert-filledSuccess': {
              backgroundColor: palette.success.main
            },
            // Monochrome filled: light bg with dark text
            '&.MuiAlert-filledMonochrome': {
              backgroundColor: palette.monochrome.alertBackground,
              color: palette.monochrome.alertContent
            },
            // Monochrome outlined: visible border
            '&.MuiAlert-outlinedMonochrome': {
              borderColor: palette.monochrome.outlineRestingBorder
            },
            // Monochrome standard: light bg with dark text
            '&.MuiAlert-standardMonochrome': {
              backgroundColor: palette.monochrome.alertBackground,
              color: palette.monochrome.alertContent
            },
            // Filled: action buttons get monochrome dark bg, inherit white text
            '&.MuiAlert-filled .MuiAlert-action .MuiButton-root': {
              backgroundColor: palette.monochrome.dark,
              color: 'inherit'
            },
            // Outlined: action buttons get outlined look using inset outline (no layout impact)
            '&.MuiAlert-outlinedError .MuiAlert-action .MuiButton-root': {
              outline: `1px solid ${alpha(palette.error.main, 0.5)}`,
              outlineOffset: '-1px',
              backgroundColor: 'transparent',
              color: palette.error.main
            },
            '&.MuiAlert-outlinedWarning .MuiAlert-action .MuiButton-root': {
              outline: `1px solid ${alpha(palette.warning.main, 0.5)}`,
              outlineOffset: '-1px',
              backgroundColor: 'transparent',
              color: palette.warning.main
            },
            '&.MuiAlert-outlinedInfo .MuiAlert-action .MuiButton-root': {
              outline: `1px solid ${alpha(palette.info.main, 0.5)}`,
              outlineOffset: '-1px',
              backgroundColor: 'transparent',
              color: palette.info.main
            },
            '&.MuiAlert-outlinedSuccess .MuiAlert-action .MuiButton-root': {
              outline: `1px solid ${alpha(palette.success.main, 0.5)}`,
              outlineOffset: '-1px',
              backgroundColor: 'transparent',
              color: palette.success.main
            },
            // Standard: action buttons get contained style with severity bg
            '&.MuiAlert-standardError .MuiAlert-action .MuiButton-root': {
              backgroundColor: palette.error.main,
              color: palette.error.contrastText,
              '&:hover': {
                backgroundColor: palette.error.dark
              }
            },
            '&.MuiAlert-standardWarning .MuiAlert-action .MuiButton-root': {
              backgroundColor: palette.warning.main,
              color: palette.warning.contrastText,
              '&:hover': {
                backgroundColor: palette.warning.dark
              }
            },
            '&.MuiAlert-standardInfo .MuiAlert-action .MuiButton-root': {
              backgroundColor: palette.info.main,
              color: palette.info.contrastText,
              '&:hover': {
                backgroundColor: palette.info.dark
              }
            },
            '&.MuiAlert-standardSuccess .MuiAlert-action .MuiButton-root': {
              backgroundColor: palette.success.main,
              color: palette.success.contrastText,
              '&:hover': {
                backgroundColor: palette.success.dark
              }
            },
            // Close icon: use light variant instead of main
            '&.MuiAlert-standardError .MuiAlert-action .MuiIconButton-root, &.MuiAlert-outlinedError .MuiAlert-action .MuiIconButton-root': {
              color: palette.error.light
            },
            '&.MuiAlert-standardWarning .MuiAlert-action .MuiIconButton-root, &.MuiAlert-outlinedWarning .MuiAlert-action .MuiIconButton-root': {
              color: palette.warning.light
            },
            '&.MuiAlert-standardInfo .MuiAlert-action .MuiIconButton-root, &.MuiAlert-outlinedInfo .MuiAlert-action .MuiIconButton-root': {
              color: palette.info.light
            },
            '&.MuiAlert-standardSuccess .MuiAlert-action .MuiIconButton-root, &.MuiAlert-outlinedSuccess .MuiAlert-action .MuiIconButton-root': {
              color: palette.success.light
            },
            '&.MuiAlert-standardMonochrome .MuiAlert-action .MuiIconButton-root, &.MuiAlert-outlinedMonochrome .MuiAlert-action .MuiIconButton-root': {
              color: palette.action.active
            },
            // Monochrome action button overrides (after generic rules for correct specificity)
            '&.MuiAlert-filledMonochrome .MuiAlert-action .MuiButton-root': {
              backgroundColor: palette.monochrome.main,
              color: palette.monochrome.alertContent,
              '&:hover': {
                backgroundColor: palette.monochrome.dark
              }
            },
            '&.MuiAlert-outlinedMonochrome .MuiAlert-action .MuiButton-root': {
              outline: `1px solid ${palette.monochrome.outlineRestingBorder}`,
              outlineOffset: '-1px',
              backgroundColor: 'transparent',
              color: palette.monochrome.contrastText
            },
            '&.MuiAlert-standardMonochrome .MuiAlert-action .MuiButton-root': {
              backgroundColor: palette.monochrome.main,
              color: palette.monochrome.contrastText,
              '&:hover': {
                backgroundColor: palette.monochrome.dark
              }
            }
          })
        },
        variants: [
        // A filled alert is the one filled surface MUI colors from
        // `getContrastText(main)` instead of the palette's own contrast
        // text, so each severity names it here — matching the backgrounds
        // pinned above.
        {
          props: {
            severity: 'error',
            variant: 'filled'
          },
          style: {
            color: palette.error.contrastText
          }
        }, {
          props: {
            severity: 'info',
            variant: 'filled'
          },
          style: {
            color: palette.info.contrastText
          }
        }, {
          props: {
            severity: 'success',
            variant: 'filled'
          },
          style: {
            color: palette.success.contrastText
          }
        }, {
          props: {
            severity: 'warning',
            variant: 'filled'
          },
          style: {
            color: palette.warning.contrastText
          }
        }]
      },
      MuiModal: {
        styleOverrides: {
          root: {
            // Scoped past invisible backdrops. Popover — and so Menu, Select and
            // Autocomplete — renders a backdrop it marks invisible; without the
            // :not() this rule outranks that class and every dropdown dims the
            // page behind it.
            '.MuiModal-backdrop:not(.MuiBackdrop-invisible)': {
              backgroundColor: palette.backdropOverlay.main
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({
            theme
          }) => ({
            // MUI draws every icon button as a circle; it follows the
            // button radius like the rest of the buttons instead.
            borderRadius: buttonRadiusValue.main,
            [theme.breakpoints.down('sm')]: {
              borderRadius: buttonRadiusValue.mobile
            },
            '&.Mui-focusVisible': {
              outline: `1px solid ${palette.primary.main}`
            },
            '.MuiSvgIcon-root': {
              transition: 'color 200ms ease-in-out'
            },
            '&:hover .MuiSvgIcon-root': {
              color: palette.text.secondary
            },
            // A tint of whatever the button sits on, not a fixed grey.
            '&:disabled': {
              backgroundColor: palette.action.disabledBackground,
              color: palette.action.disabled
            }
          })
        },
        variants: [{
          props: {
            color: 'monochrome'
          },
          style: {
            color: palette.monochrome.contrastText,
            ':hover': {
              backgroundColor: palette.action.hover
            }
          }
        }, {
          props: {
            size: 'small'
          },
          style: {
            padding: 5,
            '& svg': {
              fontSize: 20
            }
          }
        }, {
          props: {
            size: 'medium'
          },
          style: {
            padding: 10,
            '& svg': {
              fontSize: 20
            }
          }
        }]
      },
      MuiPaper: {
        styleOverrides: {
          root: ({
            theme
          }) => ({
            borderRadius: modalAndLayerRadiusValue.main,
            [theme.breakpoints.down('sm')]: {
              borderRadius: modalAndLayerRadiusValue.mobile
            }
          })
        }
      },
      // Medium (default) form fields get FIELD_PADDING on every side, replacing
      // MUI's 16.5px vertical / 14px horizontal. Small fields keep their own
      // dimensions, and the autocomplete is excluded throughout — it computes
      // its own input padding and is sized via the MuiAutocomplete override
      // below.
      //
      // Which element carries the padding follows MUI: single-line fields put it
      // on the input so an adornment stays centered instead of stretching the
      // field, multiline fields put it on the root, and an adornment moves the
      // horizontal padding to the root on that side. The input's padding is
      // zeroed wherever the root already provides it, so it is never doubled.
      //
      // The radius lives here rather than on MuiTextField so that inputs
      // composed by hand (FormControl + OutlinedInput, e.g. the password fields)
      // get the same corner radius as those rendered via TextField.
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({
            theme
          }) => ({
            borderRadius: formItemRadiusValue.main,
            [theme.breakpoints.down('sm')]: {
              borderRadius: formItemRadiusValue.mobile
            },
            '&:not(.MuiInputBase-sizeSmall):not(.MuiAutocomplete-inputRoot)': {
              '& .MuiOutlinedInput-input': {
                lineHeight: FIELD_LINE_HEIGHT
              },
              // The height replaces MUI's 1.4375em (23px) so that a single-line
              // field lands on 48px; multiline is sized by its row count.
              '&:not(.MuiInputBase-multiline) .MuiOutlinedInput-input': {
                padding: FIELD_PADDING,
                height: FIELD_LINE_HEIGHT
              },
              '&.MuiInputBase-multiline': {
                padding: FIELD_PADDING
              },
              '&.MuiInputBase-adornedStart': {
                paddingLeft: FIELD_PADDING
              },
              '&.MuiInputBase-adornedStart:not(.MuiInputBase-multiline) .MuiOutlinedInput-input': {
                paddingLeft: 0
              },
              '&.MuiInputBase-adornedEnd': {
                paddingRight: FIELD_PADDING
              },
              '&.MuiInputBase-adornedEnd:not(.MuiInputBase-multiline) .MuiOutlinedInput-input': {
                paddingRight: 0
              },
              '& .MuiOutlinedInput-input.MuiSelect-select': {
                paddingRight: SELECT_INDICATOR_SPACE
              }
            },
            // A locked field reads as a filled one: the surface becomes the
            // disabled background while the outline keeps the resting border of
            // an ordinary field rather than fading out with it. Adornments drop
            // to the disabled text color so a currency symbol carries the same
            // weight as the value beside it — MUI wraps a string adornment in a
            // Typography of its own, so the nested element needs the color too.
            '&.Mui-disabled': {
              backgroundColor: palette.action.disabledBackground,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.outlinedBorder.main
              },
              '& .MuiInputAdornment-root, & .MuiInputAdornment-root .MuiTypography-root': {
                color: palette.text.disabled
              }
            }
          }),
          // Resting border color for every outlined field — text fields, selects
          // and hand-composed inputs alike. Hover, focus and error states keep
          // MUI's own higher-specificity rules.
          notchedOutline: {
            borderColor: palette.outlinedBorder.main
          }
        }
      },
      MuiTextField: {
        defaultProps: {
          variant: fieldVariant
        }
      },
      MuiFormControl: {
        defaultProps: {
          variant: fieldVariant
        }
      },
      MuiSelect: {
        defaultProps: {
          variant: fieldVariant
        }
      },
      // The filled field, sized to the same 48px as an outlined one: the
      // label sits in the top padding and shrinks into it, and a field with
      // no label centres its text instead. Multiline fields keep MUI's own
      // padding, which sits on the root rather than the textarea.
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderTopLeftRadius: filledFieldRadius,
            borderTopRightRadius: filledFieldRadius,
            backgroundColor: palette.filledInputBackground.main,
            '&:hover, &.Mui-focused': {
              backgroundColor: palette.filledInputBackground.main
            },
            '&.Mui-disabled': {
              backgroundColor: palette.action.disabledBackground
            },
            '&::before': {
              borderBottomColor: palette.standardInputLine.main
            },
            '&:not(.MuiInputBase-sizeSmall):not(.MuiAutocomplete-inputRoot):not(.MuiInputBase-multiline)': {
              '& .MuiFilledInput-input': {
                lineHeight: FIELD_LINE_HEIGHT,
                height: FIELD_LINE_HEIGHT,
                padding: `20px ${FIELD_PADDING} 4px`
              },
              '&.MuiInputBase-hiddenLabel .MuiFilledInput-input': {
                paddingTop: FIELD_PADDING,
                paddingBottom: FIELD_PADDING
              },
              '& .MuiFilledInput-input.MuiSelect-select': {
                paddingRight: SELECT_INDICATOR_SPACE
              }
            }
          }
        }
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            '&::before': {
              borderBottomColor: palette.standardInputLine.main
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            // A filled field's label, placed for its 48px height.
            '&.MuiInputLabel-filled:not(.MuiInputLabel-sizeSmall)': {
              '&[data-shrink="false"]': {
                transform: `translate(${FIELD_PADDING}, ${FIELD_PADDING}) scale(1)`
              },
              '&[data-shrink="true"]': {
                transform: `translate(${FIELD_PADDING}, 4px) scale(0.75)`
              }
            },
            // Align the label with the input text, which now starts at
            // FIELD_PADDING rather than MUI's 14px.
            '&.MuiInputLabel-outlined:not(.MuiInputLabel-sizeSmall)': {
              // Keep the resting (un-shrunk) label vertically centered in the 48px field.
              '&[data-shrink="false"]': {
                transform: `translate(${FIELD_PADDING}, ${FIELD_PADDING}) scale(1)`
              },
              '&[data-shrink="true"]': {
                transform: `translate(${FIELD_PADDING}, -9px) scale(0.75)`
              }
            }
          }
        }
      },
      MuiAutocomplete: {
        defaultProps: {
          // The options popup is a layer like the filter poppers, so it carries
          // the same elevation rather than the Paper default of 1. Setting it
          // on the slot rather than as a boxShadow keeps the dark-mode surface
          // overlay in step with the shadow.
          slotProps: {
            paper: {
              elevation: 8
            }
          }
        },
        styleOverrides: {
          // The autocomplete splits its padding between the root and the input,
          // so it is sized separately from the MuiOutlinedInput override above.
          // The two halves add up to FIELD_PADDING, which keeps a medium field
          // at 48px with its text starting 12px from the edge. The right side is
          // left to MUI — it reserves room for the popup and clear indicators.
          inputRoot: {
            '&.MuiOutlinedInput-root:not(.MuiInputBase-sizeSmall)': {
              paddingTop: '4px',
              paddingBottom: '4px',
              paddingLeft: FIELD_PADDING,
              '& .MuiAutocomplete-input': {
                paddingTop: '8px',
                paddingBottom: '8px',
                paddingLeft: 0,
                height: FIELD_LINE_HEIGHT,
                lineHeight: FIELD_LINE_HEIGHT
              }
            }
          },
          // Detach the popup from the field the way the poppers sit below their
          // trigger.
          paper: {
            marginTop: 8
          },
          // The options are laid out like menu items so autocomplete and select
          // dropdowns read the same, and the inset keeps them clear of the
          // paper's rounded corners. The options are styled from here rather
          // than through the `option` slot because MUI sizes them with a
          // descendant selector on the listbox, which would otherwise win.
          listbox: {
            padding: 8,
            '& .MuiAutocomplete-option': {
              padding: '8px 16px',
              fontWeight: 500,
              borderRadius: formItemRadiusValue.main,
              fontSize: '14px',
              lineHeight: '24px',
              gap: '8px'
            }
          }
        }
      },
      MuiCssBaseline: {
        styleOverrides: `
          html {
            overflow-y: scroll;
          }
          html, body {
            height: 100%;
          }
          body {
            background-color: ${palette.background.default};
          }
          .MuiPickersPopper-root {
            margin-top: 10px !important;
            z-index: 1090 !important;
          }
          .MuiPickersPopper-root .MuiPaper-root {
            border-radius: 20px;
          }
          .MuiPickersPopper-root .MuiPaper-root .MuiTypography-subtitle1 {
            font-weight: 700;
          }
          .MuiDateRangePickerDay-root.MuiDateRangePickerDay-rangeIntervalDayHighlight {
            background-color: ${alpha(palette.primary.main, 0.08)};
          }
          .MuiButtonBase-root.MuiPickersDay-root.Mui-selected:focus {
            background-color: ${palette.primary.main};
          }
          .MuiPickersPopper-root .MuiPaper-root .MuiPickersDay-root {
            font-weight: 500;
          }
          .MuiPickersPopper-root .MuiPaper-root .MuiDateRangePickerViewDesktop-container {
            border-color: transparent;
          }
          body .SnackbarContainer-root.SnackbarContainer-top {
            top: 73px;
          }
          input[type="password"] {
            font-family: Verdana;
            letter-spacing: 0.2em;
            -webkit-text-stroke-width: 0.1em;
          }
        `
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              color: palette.primary.main
            }
          }
        }
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            // Unchecked circle; the checked and disabled classes keep their
            // own colors, which are more specific than this.
            color: palette.monochrome.contrastText,
            '&.Mui-focusVisible': {
              color: palette.primary.main
            }
          }
        }
      },
      MuiSwitch: {
        styleOverrides: iosSwitches ? {
          // A pill track the thumb sits inside, filled with the switch's
          // color when on, the way iOS draws a switch.
          root: {
            width: 42,
            height: 26,
            padding: 0
          },
          switchBase: {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              '& + .MuiSwitch-track': {
                opacity: 1,
                border: 0
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5
              }
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: isDark ? 0.3 : 0.7
            }
          },
          thumb: {
            boxSizing: 'border-box',
            width: 22,
            height: 22,
            boxShadow: 'none',
            backgroundColor: palette.common.white
          },
          track: {
            borderRadius: 13,
            backgroundColor: isDark ? palette.grey[800] : palette.grey[300],
            opacity: 1
          }
        } : {
          // MUI tints the off-state track with plain black, which reads
          // far darker than the design's light grey. The checked track
          // keeps MUI's color and only drops to the same opacity as the
          // off state.
          track: {
            backgroundColor: palette.grey[500],
            opacity: 0.38
          },
          root: {
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              opacity: 0.38
            }
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            '&:-webkit-autofill': {
              boxShadow: `0 0 0 1000px ${palette.background.paper} inset !important`,
              WebkitTextFillColor: `${palette.text.primary} !important`
            }
          }
        }
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            body1Bold: 'p',
            body1Medium: 'p',
            body2Bold: 'p',
            body2Medium: 'p',
            body3: 'p',
            body3Medium: 'p',
            body3Bold: 'p'
          }
        }
      },
      MuiDialog: {
        // Fading is MUI's own default; the slides swap in `Slide`, from the
        // right edge or from the bottom.
        ...(modalAnimation && modalAnimation !== 'fade' && {
          defaultProps: {
            slots: {
              transition: Slide
            },
            slotProps: {
              transition: {
                direction: modalAnimation === 'slideSide' ? 'left' : 'up'
              }
            }
          }
        }),
        styleOverrides: {
          root: {
            '.MuiBackdrop-root': {
              backgroundColor: palette.backdropOverlay.main
            }
          },
          paper: {
            // The paper is only focused to move focus into the dialog, so a ring
            // around the whole modal reads as a stray stroke. Controls inside
            // keep their own focus styling.
            '&:focus': {
              outline: 'none'
            },
            '&:focus-visible': {
              outline: 'none'
            }
          }
        }
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true
        },
        styleOverrides: {
          tooltip: {
            backgroundColor: palette.snackbarBackground.main,
            color: isDark ? palette.common.black : palette.common.white,
            fontSize: 10,
            fontWeight: 500,
            lineHeight: '14px',
            padding: '4px 8px',
            borderRadius: 6
          },
          arrow: {
            color: palette.snackbarBackground.main
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          // An app bar spans the viewport edge to edge, so it drops the modal
          // radius the MuiPaper override gives every surface, on both sides of
          // the breakpoint that override switches at.
          root: ({
            theme
          }) => ({
            borderRadius: 0,
            [theme.breakpoints.down('sm')]: {
              borderRadius: 0
            }
          })
        }
      },
      MuiBadge: {
        styleOverrides: {
          badge: ({
            ownerState,
            theme
          }) => ({
            // The dot variant stays a circle whatever the button radius.
            ...(ownerState.variant !== 'dot' && {
              borderRadius: buttonRadiusValue.main,
              [theme.breakpoints.down('sm')]: {
                borderRadius: buttonRadiusValue.mobile
              }
            }),
            // MUI gives the default color no fill at all.
            ...(ownerState.color === 'default' && {
              backgroundColor: palette.monochrome.dark,
              color: palette.text.primary
            })
          })
        }
      },
      MuiTableCell: {
        styleOverrides: {
          // MUI derives an opaque grey from the divider for cell borders,
          // which reads heavier than every other divider and ignores the
          // surface it sits on.
          root: {
            borderBottomColor: palette.divider
          }
        }
      },
      MuiFab: {
        styleOverrides: {
          root: ({
            theme
          }) => ({
            borderRadius: buttonRadiusValue.main,
            [theme.breakpoints.down('sm')]: {
              borderRadius: buttonRadiusValue.mobile
            },
            variants: [{
              props: {
                variant: 'extended'
              },
              style: {
                gap: 8,
                paddingLeft: 16,
                paddingRight: 24
              }
            }, {
              props: {
                variant: 'extended',
                size: 'small'
              },
              style: {
                paddingLeft: 8,
                paddingRight: 16
              }
            }, {
              props: {
                size: 'large'
              },
              style: {
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '26px',
                letterSpacing: '0.2px'
              }
            }]
          })
        }
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: ({
            theme
          }) => ({
            variants: [{
              props: {
                shape: 'rounded'
              },
              style: {
                borderRadius: formItemRadiusValue.main,
                [theme.breakpoints.down('sm')]: {
                  borderRadius: formItemRadiusValue.mobile
                }
              }
            }, {
              props: {
                variant: 'outlined'
              },
              style: {
                borderColor: palette.outlinedBorder.main
              }
            }, {
              props: {
                variant: 'text',
                color: 'standard'
              },
              style: {
                '&.Mui-selected': {
                  backgroundColor: palette.action.hover
                }
              }
            }]
          })
        }
      },
      MuiRating: {
        // An empty star is the same filled shape in a muted color, not an
        // outline.
        defaultProps: {
          emptyIcon: <StarIcon fontSize="inherit" />
        },
        styleOverrides: {
          iconFilled: {
            color: palette.ratingActive.main
          },
          iconEmpty: {
            color: palette.outlinedBorder.main
          },
          root: {
            '&.Mui-disabled': {
              opacity: 0.5
            }
          }
        }
      }
    }
  };
  if (customStyles?.components?.MuiCssBaseline?.styleOverrides) {
    customStyles.components.MuiCssBaseline.styleOverrides = `${result.components?.MuiCssBaseline?.styleOverrides ?? ''}${customStyles.components.MuiCssBaseline.styleOverrides}`;
  }
  return defaultsDeep(customStyles, result);
}
export function ThemeMeta({
  appConfig,
  title,
  colorScheme
}: {
  appConfig: any;
  title?: string;
  colorScheme: 'light' | 'dark';
}) {
  const theme = defaultsDeep({}, colorScheme === 'dark' ? appConfig.darkTheme : {}, appConfig.theme);
  // The favicon sits in the browser's tab strip, which follows the OS colour
  // scheme rather than the page's, so the dark one is picked by media query.
  // A server render can't know which scheme the visitor's system picks, so
  // load the fonts of both.
  const fontFaces: string[] = [...new Set<string>([...(theme.fontFaces ?? []), ...(appConfig.darkTheme?.fontFaces ?? [])])];
  const favicon = appConfig.theme.faviconUrl ?? '/favicon.ico';
  const darkFavicon = appConfig.darkTheme?.faviconUrl;
  return <>
      {title && <title>{title}</title>}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {darkFavicon && darkFavicon !== favicon ? <>
          <link rel="icon" href={favicon} media="(prefers-color-scheme: light)" />
          <link rel="icon" href={darkFavicon} media="(prefers-color-scheme: dark)" />
        </> : <link rel="shortcut icon" href={favicon} />}

      {/* <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
       />
       <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
        media="(prefers-color-scheme: light)"
       />
       <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
        media="(prefers-color-scheme: light)"
       />
       <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32-dark.png"
        media="(prefers-color-scheme: dark)"
       />
       <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16-dark.png"
        media="(prefers-color-scheme: dark)"
       /> 
       <link rel="manifest" href="/site.webmanifest" />
       <link
        rel="mask-icon"
        href="/safari-pinned-tab.svg"
        color={theme.primaryColor}
       />
       <meta name="msapplication-TileColor" content={theme.primaryColor} /> */}

      <meta name="theme-color" content="#000000" />

      {fontFaces.some(f => f.includes('googleapis.com')) && <>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        </>}
      {fontFaces.map(fontFace => <Fragment key={fontFace}>
          <link href={fontFace} rel="preload" as="style" />
          <link href={fontFace} rel="stylesheet" />
        </Fragment>)}
    </>;
}